import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Users, MapPin, Plus, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/communities/")({
  component: CommunitiesIndex,
  head: () => {
    const title = "Xamseen — Communities";
    const description =
      "Browse Xamseen communities by city and country — join local groups documenting issues and sharing solutions where you live.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://xamseen.com/communities" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: "https://xamseen.com/communities" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Xamseen Communities",
            description,
            url: "https://xamseen.com/communities",
          }),
        },
      ],
    };
  },
});


interface Community {
  id: string; name: string; slug: string; description: string | null;
  country: string; region: string | null; city: string | null;
  member_count: number; created_by: string;
}

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

function CommunitiesIndex() {
  const { user } = useAuth();
  const [items, setItems] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("communities")
      .select("id, name, slug, description, country, region, city, member_count, created_by")
      .order("member_count", { ascending: false })
      .limit(100);
    setItems((data ?? []) as Community[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      c.country.toLowerCase().includes(q);
  });

  // Group by country
  const groups: Record<string, Community[]> = {};
  filtered.forEach((c) => { (groups[c.country] ||= []).push(c); });

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Communities</h1>
          <p className="text-sm text-muted-foreground">Country → Region → City. Find yours or start one.</p>
        </div>
        {user && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="h-4 w-4" /> Create community</Button>
            </DialogTrigger>
            <CreateCommunityDialog onCreated={() => { setCreateOpen(false); load(); }} />
          </Dialog>
        )}
      </div>

      <div className="mb-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by city or country…" className="pl-9" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="font-display font-medium">No communities yet</p>
          <p className="mt-1 text-sm text-muted-foreground">{user ? "Be the first to create one." : "Sign in to create one."}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groups).sort(([a],[b]) => a.localeCompare(b)).map(([country, list]) => (
            <section key={country}>
              <h2 className="mb-3 font-display text-sm font-medium uppercase tracking-wider text-muted-foreground">{country}</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {list.map((c) => (
                  <Link
                    key={c.id}
                    to="/communities/$slug" params={{ slug: c.slug }}
                    className="group rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-card)] transition-colors hover:border-foreground/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display text-base font-semibold tracking-tight group-hover:text-primary">{c.name}</div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {[c.city, c.region, c.country].filter(Boolean).join(", ")}
                        </div>
                        {c.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{c.description}</p>}
                      </div>
                      <div className="shrink-0 rounded-md bg-muted px-2 py-1 text-xs font-medium">
                        <Users className="mr-1 inline h-3 w-3" />{c.member_count}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function CreateCommunityDialog({ onCreated }: { onCreated: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const slug = slugify(name);
    if (!slug) return toast.error("Please enter a valid name");
    setBusy(true);
    const { data, error } = await supabase
      .from("communities")
      .insert({
        name: name.trim(), slug, country: country.trim(),
        region: region.trim() || null, city: city.trim() || null,
        description: description.trim() || null, created_by: user.id,
      })
      .select("id, slug")
      .single();

    if (!error && data) {
      await supabase.from("community_members").insert({ community_id: data.id, user_id: user.id, role: "admin" });
    }
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Community created");
    onCreated();
    navigate({ to: "/communities/$slug", params: { slug: data!.slug } });
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle className="font-display">Create a community</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="grid gap-4 py-2">
        <div className="grid gap-1.5">
          <Label>Community name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Yaba Lagos" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-1.5"><Label>Country</Label>
            <Input required value={country} onChange={(e) => setCountry(e.target.value)} maxLength={56} placeholder="Nigeria" />
          </div>
          <div className="grid gap-1.5"><Label>Region</Label>
            <Input value={region} onChange={(e) => setRegion(e.target.value)} maxLength={56} placeholder="Lagos" />
          </div>
          <div className="grid gap-1.5"><Label>City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} maxLength={56} placeholder="Yaba" />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} rows={3} />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
